import { router } from "@inertiajs/react";
import { AlertCircle, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { bulk_destroy_bizminer_admin_organization_users_path } from "@/routes";
import type { BizminerUser } from "@/types/serializers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  users: BizminerUser[];
  onSuccess: () => void;
}

function UserList({ users }: { users: BizminerUser[] }) {
  const displayUsers = users.slice(0, 5);
  const remaining = users.length - displayUsers.length;

  return (
    <ul className="text-sm space-y-1">
      {displayUsers.map((user) => (
        <li key={user.id} className="text-muted-foreground">
          • {user.name} ({user.email_address})
        </li>
      ))}
      {remaining > 0 && (
        <li className="text-muted-foreground">...and {remaining} more</li>
      )}
    </ul>
  );
}

export default function BulkDeleteDialog({
  open,
  onOpenChange,
  organizationId,
  users,
  onSuccess,
}: Props) {
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleDelete = () => {
    setProcessing(true);
    setErrors([]);
    router.delete(
      bulk_destroy_bizminer_admin_organization_users_path(organizationId),
      {
        data: { ids: users.map((u) => u.id) },
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: (errors) => {
          const baseErrors = errors.base as string[] | undefined;
          if (baseErrors) {
            setErrors(baseErrors);
          }
        },
        onFinish: () => setProcessing(false),
      },
    );
  };

  const handleClose = () => {
    setErrors([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <DialogTitle>Delete Users</DialogTitle>
          </div>
          <DialogDescription>
            Are you sure you want to delete {users.length}{" "}
            {users.length === 1 ? "user" : "users"}? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
          <UserList users={users} />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={processing}
          >
            {processing ? "Deleting..." : `Delete ${users.length} Users`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
